import { format, intervalToDuration } from 'date-fns'
import { asArray, asMoney } from "lib/utilities"
import { isDate, isNil, isPlainObject, isString, mapValues, omit } from 'lodash-es'
import { metadataByPath } from "../form"
import { default as checkConditions } from './check_conditions'
import { translateValue } from "./utilities"

import { deserialize as deserializeDuration } from 'components/form_field/types/duration'

const isEmpty = (val) => isNil(val) || val === false || String(val).match(/^\s*$/)
const isntEmpty = (val) => !isEmpty(val)

const ROUND_TO = {
  'quarter': 4.0,
  'half': 2.0,
  'whole': 1.0,
}
const daysInMonth = (year, month) => new Date(year, month, 0).getDate()

/*
 * [ path, path ] == { func: "CONCAT", values: [ path, path ], delimiter: " "}
 * { func: "UPCASE", value: path }
 * { func: FUNCTION_NAME, values: [ path, path] }
 * { func: "CONCAT", values: [path, path], delimiter: " "}
 * { func: "MAP", map: "CAPITALIZE", values: [ path, path ..]}
 * { func: "FILTER", filter: "EXISTS", values: [ path, path ]}
 * { func: "MONEY", value: "path" }
 * { func: "YEAR", value: STRING | DATE }
 * { func: "YEAR_2", value: STRING | DATE}
 * { func: "MONTH", value: STRING | DATE }
 * { func: "DAY", value: STRING | DATE}
 * { func: "NOW" }
 */

function renderTemplate(template, request) {
  if (Array.isArray(template)) template = { func: 'CONCAT', values: template }

  if (typeof template != 'object') return translateValue(template, request)

  if (isPlainObject(template.variables)) {
    request.pathMap = {
      ...request.pathMap,
      ...mapValues(template.variables, (varTemplate) => renderTemplate(varTemplate, request)),
    }
  }

  const method = String(template.func).toLowerCase()
  
  template = omit(template, ["variables", "func"])

  if (CONDITIONALS[method]) {
    return renderConditional(method, template, request)
  } else if (ITERATORS[method]) {
    return renderIterator(method, template, request)
  } else {
    return renderSingle(method, template, request)
  }
}

function renderSingle(method, template, request) {
  const values = asArray(template.values)
    .map((path) => renderTemplate(path, request))
    .flat()
  const value = template.value ? renderTemplate(template.value, request) : null

  const compiled = { ...template, values, value, request }

  return FUNCTIONS._call(method, compiled, request)
}

function renderConditional(method, template, request) {
  const condition = template.if
  const branch = checkConditions(condition, request) ? template.then : template.else
  return branch ? renderTemplate(branch, request) : ''
}

function renderIterator(method, template, request) {
  let values = iteratableValues(template, request)

  const compiled = omit({ ...template, values }, ['value'])
  return FUNCTIONS._call(method, compiled, request) || []
}

function iteratableValues(template, request) {
  if (template['values']) return template['values']

  const value = template['value']
  if (Array.isArray(value)) return value
  if (!isString(value)) return []

  const length = parseInt(translateValue(`${value}[length]`, request)) || 0
  return new Array(length).fill(null).map((_, i) => `${value}[${i}]`)
}

const ITERATORS = {
  map: true,
  merge: true,
  filter: true,
}

const CONDITIONALS = {
  if: true
}

const FUNCTIONS = {
  _call(method, options, request) {
    if (!FUNCTIONS[method]) return ''

    switch (FUNCTIONS[method].length) {
      case 0:
        return FUNCTIONS[method]()
      case 1:
        return FUNCTIONS[method](options)
      case 2:
        return FUNCTIONS[method](options, request)
    }
  },

  filter(options, request) {
    const { values, filter } = options
    filter = FUNCTIONS[filter] ? filter : 'isPresent'

    return values.filter((value) => FUNCTIONS[filter]({ ...options, value }))
  },

  map(options, request) {
    const { values, map, allow_empty } = options
    func = FUNCTIONS[map] ? map : 'self'

    const mapped = values.filter(isntEmpty).map((value) => renderTemplate({ func, value }, request))
    return allow_empty ? mapped : mapped.filter(isntEmpty)
  },

  merge(options, request) {
    let values = options['values']
    if (!value) return []

    values = values.map((value) => renderTemplate(value, request))
    return values.reduce((a, b) => a.concat(b), [])
  },

  concat({ values, delimiter = ' ' }) {
    return values.join(delimiter)
  },

  upcase({ value }) {
    return String(value).toUpperCase()
  },

  downcase({ value }) {
    return String(value).toLowerCase()
  },

  capitalize({ value }) {
    value = String(value)
    return value[0].toUpperCase() + value.substr(1)
  },

  money({ value, currency = '$' }) {
    value = parseInt(value)
    return asMoney(value, { currency })
  },

  now(_) {
    return new Date()
  },

  year({ value }) {
    return formatDate('yyyy', value)
  },

  year_2({ value }) {
    return formatDate('yy', value)
  },

  month({ value }) {
    return formatDate('M', value)
  },

  month_name({ value }) {
    return formatDate('MMMM', value)
  },

  month_short_name({ value }) {
    return formatDate('MMM', value)
  },

  date({ value }) {
    return formatDate('d', value)
  },

  date_with_suffix({ value }) {
    return formatDate('do', value)
  },

  day_of_week({ value }) {
    return formatDate('EEEE', value)
  },

  short_day_of_week({ value }) {
    return formatDate('EEE', value)
  },

  letter_of_week({ value }) {
    return formatDate('EEEEE', value)
  },

  duration_between({ values, inclusive }) {
    const start = generateDate(values[0])
    const end = generateDate(values[1])
    if (!start || !end) return { years: 0, months: 0, days: 0 }

    return durationBetween({ start, end, inclusive })
  },

  duration_years({ value }) {
    return generateDuration(value).years
  },

  duration_months({ value }) {
    return generateDuration(value).months
  },

  duration_days({ value }) {
    return generateDuration(value).days
  },

  date_plus_duration({ values }) {
    const date = generateDate(values[0])
    const duration = generateDuration(values[1])

    return new Date(
      date.getFullYear() + duration.years,
      date.getMonth() + duration.months,
      date.getDate() + duration.days,
      0,
      0,
      0
    )
  },

  date_minus_duration({ values }) {
    const date = generateDate(values[0])
    const duration = generateDuration(values[1])

    return new Date(
      date.getFullYear() - duration.years,
      date.getMonth() - duration.months,
      date.getDate() - duration.days,
      0,
      0,
      0
    )
  },

  date_difference_years({ values, inclusive }) {
    const start = generateDate(values[0])
    const end = generateDate(values[1])
    if (!start || !end) return ''

    return durationBetween({ start, end, inclusive }).years
  },

  date_difference_months({ values, inclusive }) {
    const start = generateDate(values[0])
    const end = generateDate(values[1])
    if (!start || !end) return ''

    return durationBetween({ start, end, inclusive }).months
  },

  date_difference_days({ values, inclusive }) {
    const start = generateDate(values[0])
    const end = generateDate(values[1])
    if (!start || !end) return ''

    return durationBetween({ start, end, inclusive }).days
  },

  rounded_duration({ round_by, round_to, round_op, value }) {
    round_by = round_by || 'months'
    round_to = ROUND_TO[round_to] || (round_by === 'months' ? ROUND_TO['whole'] : ROUND_TO['quarter'])
    round_op = round_op || 'avg'

    const duration = generateDuration(value)

    let rounding = (duration.days * 1.0) / 31
    if (round_by === 'years') rounding = (duration.months + rounding) / 12.0

    rounding = rounding * round_to
    switch (round_op) {
      case 'floor':
        rounding = Math.floor(rounding)
        break
      case 'ceil':
        rounding = Math.ceil(rounding)
        break
      case 'avg':
        rounding = Math.round(rounding)
        break
    }

    rounding /= round_to
    duration.days = 0

    if (round_by === 'months') {
      duration.months += rounding
      if (duration.months === 12) {
        duration.months = 0
        duration.years += 1
      }
    }

    if (round_by === 'years') {
      duration.months = 0
      duration.years += rounding
    }

    return duration
  },

  template({ values, template }) {
    if (values.every((value) => isEmpty(value))) return ''

    return values.reduce((current, value, index) => {
      const val = isEmpty(value) ? '' : String(value)
      return current.replace(`{{${index}}}`, val)
    }, template)
  },

  lookup_in_options({ path, value, request: { metadata } = {} }) {
    if (!metadata) return ''

    const { options } = metadataByPath(metadata, path) || {}
    if (!options) return ''

    return isPlainObject(options) ? options[value] || '' : ''
  },

  self: ({ value }) => value,

  // BOOLEANS

  isPresent: ({ value }) => value && !String(value).match(/^\s*$/),

  // MATHS

  // Numeric values => sum (null treated as 0)
  // Non-numeric string present => string concatenation (null treated as "")
  add({ values = [] }) {
    if (values.length === 0) return null

    if (values.every((v) => isNil(v) || isNumeric(v))) {
      const result = values.reduce((acc, v) => acc + (isNil(v) ? 0 : Number(v)), 0)
      return isFinite(result) ? result : null
    }

    return values.map((v) => (isNil(v) ? '' : String(v))).join('')
  },

  // Two dates => days between them
  // Date - number => ISO date shifted backward
  // Two numbers => numeric difference
  // Null or non-numeric string => null
  subtract({ values = [] }) {
    if (values.length < 2) return null

    const [left, right] = values
    const leftDate = parseDate(left)
    const rightDate = parseDate(right)

    if (leftDate && rightDate) {
      return Math.round(Math.abs(leftDate - rightDate) / (1000 * 60 * 60 * 24))
    } else if (leftDate && isNumeric(right)) {
      const result = new Date(leftDate)
      result.setDate(result.getDate() - Number(right))
      return formatISODate(result)
    } else if (isNumeric(left) && isNumeric(right)) {
      const result = Number(left) - Number(right)
      return isFinite(result) ? result : null
    }

    return null
  },

  // Both values must be numeric or numeric strings.
  // Strings, dates, booleans, or null => null
  multiply({ values = [] }) {
    if (!values.length || !values.every(isNumeric)) return null

    const result = values.map(Number).reduce((a, b) => a * b)
    return isFinite(result) ? result : null
  },

  // Both values must be numeric or numeric strings.
  // Division by zero => null
  // Strings, dates, booleans, or null => null
  divide({ values = [] }) {
    if (!values.length || !values.every(isNumeric)) return null

    return values.map(Number).reduce((acc, value) => {
      if (acc === null || value === 0) return null
      const result = acc / value
      return isFinite(result) ? result : null
    })
  },

  // Returns the number of days between two ISO dates.
  date_diff({ values = [] }) {
    if (values.length < 2) return null

    const startDate = parseDate(values[0])
    const endDate = parseDate(values[1])
    if (!startDate || !endDate) return null

    return Math.round(Math.abs(endDate - startDate) / (1000 * 60 * 60 * 24))
  },
}

export default renderTemplate

const ISO_DATE_PATTERN = /^\d{4}-\d{1,2}-\d{1,2}$/

function isNumeric(value) {
  if (isNil(value)) return false
  if (typeof value === 'number') return isFinite(value)
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (trimmed === '') return false
  return !isNaN(Number(trimmed))
}

function parseDate(value) {
  if (typeof value !== 'string' || !ISO_DATE_PATTERN.test(value.trim())) return null
  return generateDate(value)
}

function formatISODate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function generateDate(value) {
  if (!value) return null
  if (isDate(value)) return value
  if (!isString(value) || isEmpty(value)) return null

  const [date, time] = value.split(' ')
  const [year, month, day] = date.split('-').map((i) => parseInt(i) || 0)
  const [hour, minute, second] = (time || '').split(';').map((i) => parseInt(i) || 0)

  return new Date(year || 1, (month || 1) - 1, day || 1, hour || 0, minute || 0, second || 0)
}

function durationBetween({ start, end, inclusive }) {
  end = new Date(end.valueOf())
  if (inclusive) end.setDate(end.getDate() + 1)

  return intervalToDuration({ start, end })
}

function generateDuration(value) {
  if (isString(value)) value = deserializeDuration(value)
  if (!isPlainObject(value)) value = {}

  return { years: value.years || 0, months: value.months || 0, days: value.days || 0 }
}

function formatDate(template, value) {
  const date = generateDate(value)
  if (!date) return ''

  return format(date, template)
}
